// OptimizedBannerListCarousel.tsx (IntersectionObserver ile autoplay sadece görünürken aktif)
'use client';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { HomePageBanner } from '@/models/HomePage';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import OptimizedBannerView from './OptimizedBannerView';

interface OptimizedBannerListCarouselProps {
  banners?: HomePageBanner[];
  autoplayDelay?: number;
  isPriority?: boolean;
  fillHeight?: boolean;
  objectFit?: 'cover' | 'contain';
}

export default function OptimizedBannerListCarousel({
  banners,
  autoplayDelay = 3000,
  isPriority = false,
  fillHeight = false,
  objectFit = 'cover',
}: OptimizedBannerListCarouselProps) {
  const [autoplayPlugin, setAutoplayPlugin] = useState<any>(undefined);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // IntersectionObserver ile sadece görünürken autoplay import et
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '200px' },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (isVisible && banners && banners.length > 1) {
      const run = () =>
        import('embla-carousel-autoplay').then((module) => {
          if (isMounted) setAutoplayPlugin(() => module.default);
        });
      (window as any).requestIdleCallback ? requestIdleCallback(run) : setTimeout(run, 300);
    }
    return () => {
      isMounted = false;
    };
  }, [isVisible, banners]);

  const processedBanners = useMemo(() => {
    if (!banners) return [];
    return banners
      .filter((banner) => {
        if (!banner) return false;
        if (banner?.badgeEndTime) {
          const left = Math.floor(
            (new Date(banner.badgeEndTime).getTime() - new Date().getTime()) / 1000,
          );
          return left > 10;
        }
        return true;
      })
      .map((banner, index) => ({
        ...banner,
        originalIndex: index,
      }));
  }, [banners]);

  if (!processedBanners.length) return null;

  const plugins =
    processedBanners.length > 1 && typeof autoplayPlugin === 'function'
      ? [
          autoplayPlugin({
            delay: autoplayDelay,
            stopOnInteraction: false,
            stopOnMouseEnter: true,
          }),
        ]
      : [];

  return (
    <div ref={ref} className={`group/banner w-full ${fillHeight ? 'md:h-full' : ''}`}>
      <Carousel
        className={`w-full ${fillHeight ? 'md:h-full' : ''}`}
        opts={{
          align: 'start',
          loop: processedBanners.length > 1,
          skipSnaps: false,
          watchDrag: false,
        }}
        plugins={plugins}
      >
        <CarouselContent
          className={fillHeight ? 'md:h-full md:ml-0' : undefined}
        >
          {processedBanners.map((banner, index) => (
            <CarouselItem
              key={`${banner.id}-${banner.originalIndex}`}
              className={fillHeight ? 'md:h-full md:pl-0' : undefined}
            >
              <OptimizedBannerView
                banner={banner}
                inCarousel={true}
                index={index}
                isFirstBanner={index === 0}
                isPriority={isPriority && index === 0}
                isLCP={isPriority && index === 0}
                fillHeight={fillHeight}
                objectFit={objectFit}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        {processedBanners.length > 1 && (
          <>
            <CarouselPrevious className="hidden md:flex left-3 w-10 h-14 rounded-xl bg-white/75 hover:bg-white border-none text-gray-700 hover:text-gray-900 opacity-0 group-hover/banner:opacity-100 transition-opacity duration-200">
              <ChevronLeft className="h-6 w-6" />
            </CarouselPrevious>
            <CarouselNext className="hidden md:flex right-3 w-10 h-14 rounded-xl bg-white/75 hover:bg-white border-none text-gray-700 hover:text-gray-900 opacity-0 group-hover/banner:opacity-100 transition-opacity duration-200">
              <ChevronRight className="h-6 w-6" />
            </CarouselNext>
          </>
        )}
      </Carousel>
    </div>
  );
}
